import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { helpElement } from '../../../help/attrs';

export type StatTone = 'default' | 'good' | 'warning' | 'critical';

export interface StatTileProps {
    label: string;
    value: string | number;
    sub?: string;
    tone?: StatTone;
    /** Goal figure shown under the meter; a tile with a target renders a progress meter. */
    target?: number;
    /** Progress toward the target as a 0–100 percentage; clamped when out of range. */
    meter?: number;
    /** Overrides the default "Target: {target}" caption, e.g. when the meter tracks a derived metric. */
    targetLabel?: string;
    /**
     * Unit indicator rendered just before the value, muted and smaller — e.g. the currency symbol
     * on a money tile. Without it a bare "12,400.00" beside a count tile reads as a quantity.
     */
    unit?: string;
    /** Page behind the number; when set the whole tile is a link to it. */
    to?: string;
    /** Help-mode key, rendered as `data-help` on the tile root. */
    help?: string;
    /** A literal help key written on the tile where it is used (the key-coverage scan reads these). */
    'data-help'?: string;
}

const TONE_COLOR: Record<StatTone, string> = {
    default: 'text.primary',
    good: 'success.main',
    warning: 'warning.main',
    critical: 'error.main',
};

const LINK_SX = {
    display: 'block', color: 'inherit', textDecoration: 'none', borderRadius: 1, cursor: 'pointer',
    '&:hover': { bgcolor: 'action.hover' },
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
} as const;

export default function StatTile({ label, value, sub, tone = 'default', target, meter, targetLabel, unit, to, help, ...rest }: StatTileProps) {
    const linkProps = to ? { component: RouterLink, to, 'data-testid': 'stat-tile-link' } : {};
    return (
        <Box data-tone={tone} data-help-solo="" {...linkProps} {...rest} {...helpElement(help)} sx={{ p: 1.5, minWidth: 'min-content', flex: '1 1 96px', maxWidth: '100%', overflowWrap: 'anywhere', ...(to ? LINK_SX : {}) }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', overflowWrap: 'break-word', wordBreak: 'normal', hyphens: 'auto' }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: 'Archivo, sans-serif', fontWeight: 700, fontSize: { xs: 22, sm: 28 }, lineHeight: 1.1, color: TONE_COLOR[tone], overflowWrap: 'normal' }}>
                {unit && (
                    <Box component="span" sx={{ fontSize: { xs: 15, sm: 18 }, fontWeight: 500, color: 'text.secondary', mr: 0.5 }}>
                        {unit}
                    </Box>
                )}
                {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            {meter != null && (
                <Box sx={{ mt: 0.75 }}>
                    <Box
                        data-testid="stat-tile-meter"
                        sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                height: '100%',
                                width: `${Math.max(0, Math.min(100, meter))}%`,
                                bgcolor: TONE_COLOR[tone],
                                borderRadius: 2,
                            }}
                        />
                    </Box>
                    {target != null && (
                        <Typography variant="caption" color="text.secondary">
                            {targetLabel ?? `Target: ${target}`}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
